/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useGetFeaturedProducts } from "@/api/queries";

const defaultSlides = [
  {
    id: "default-1",
    name: "Summer Collection",
    description: "Explore the hottest looks of the season and enjoy up to 40% off!",
    imageSrc:
      "https://images.unsplash.com/photo-1583316174775-bd6dc0e9f298?q=80&w=2940&auto=format&fit=crop",
    link: "/shop/summer-collection",
  },
  {
    id: "default-2",
    name: "Tech Essentials",
    description: "Upgrade your workspace with our new arrivals in tech gear.",
    imageSrc:
      "https://images.unsplash.com/photo-1681505805363-73f08e4a94d7?q=80&w=2950&auto=format&fit=crop",
    link: "/shop/electronics",
  },
  {
    id: "default-3",
    name: "Home Makeover",
    description: "Transform your living space with fresh new decor ideas.",
    imageSrc:
      "https://images.unsplash.com/photo-1687180498602-5a1046defaa4?q=80&w=2940&auto=format&fit=crop",
    link: "/shop/home-kitchen",
  },
];

export const Hero = () => {
  const { data } = useGetFeaturedProducts();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slides, setSlides] = useState<any[]>(defaultSlides);
  const isMobile = useIsMobile();

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const minSwipeDistance = 50;

  useEffect(() => {
    if (data?.data?.length) {
      const shuffled = [...data.data].sort(() => Math.random() - 0.5);
      const formatted = shuffled
        .map((p) => ({
          id: p.id,
          name: p.name,
          description: p.description || "Discover this standout item from our latest collection.",
          imageSrc: p.images?.[0]?.url,
          link: `/product/${p.id}`,
        }))
        .filter((p) => p.imageSrc); // skip if no image
      setSlides(formatted.length > 0 ? formatted : defaultSlides);
    }
  }, [data]);

  useEffect(() => {
    if (!slides.length) return;
    const timer = setTimeout(() => handleNextSlide(), isMobile ? 4000 : 7000);
    return () => clearTimeout(timer);
  }, [currentSlide, isMobile, slides]);

  const handlePrevSlide = () => {
    if (isAnimating || !slides.length) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handleNextSlide = () => {
    if (isAnimating || !slides.length) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 500);
  };

  const goToSlide = (index: number) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handleTouchStart = (e: any) => (touchStartX.current = e.touches[0].clientX);
  const handleTouchMove = (e: any) => (touchEndX.current = e.touches[0].clientX);
  const handleTouchEnd = () => {
    if (!isMobile) return;
    const distance = touchStartX.current - touchEndX.current;
    if (Math.abs(distance) < minSwipeDistance) return;
    if (distance > 0) handleNextSlide();
    else handlePrevSlide();
  };

  const current = slides[currentSlide];

  return (
    <div
      className='relative w-full overflow-hidden min-h-[80vh]'
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Image slides */}
      {slides.map((p, i) => (
        <div
          key={p.id}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            i === currentSlide ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          {/* Desktop: background image */}
          {/* <div
            className='hidden md:block absolute inset-0 bg-center bg-no-repeat bg-contain'
            style={{ backgroundImage: `url(${p.imageSrc})` }}
          /> */}
          <img
            src={p.imageSrc}
            alt={p.name}
            className='w-full h-[80vh] object-cover object-center'
          />
          {/* Gradient overlay */}
          <div className='absolute inset-0 bg-gradient-to-t from-black/50 to-transparent' />
        </div>
      ))}

      {/* Minimal text overlay */}
      <div className='absolute bottom-0 left-0 right-0 z-10 p-6 md:p-8'>
        <div className='max-w-2xl text-white'>
          <h1 className='text-2xl md:text-3xl lg:text-4xl font-bold leading-tight drop-shadow-sm'>
            {current.name}
          </h1>
          <p className='mt-2 text-sm md:text-base opacity-90 max-w-lg drop-shadow-sm'>
            {current.description}
          </p>
          <div className='mt-4 flex flex-wrap gap-3'>
            <Button
              size='sm'
              className='group bg-white text-black hover:bg-gray-100 font-medium px-5'
              asChild
            >
              <a href={current.link}>
                Shop Now
                <ArrowRight className='ml-1.5 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5' />
              </a>
            </Button>
            <Button
              variant='outline'
              size='sm'
              className='border-white/40 text-white bg-white/10 font-medium px-5'
              asChild
            >
              <a href='/shop'>View All</a>
            </Button>
          </div>
        </div>
      </div>

      {/* Desktop arrows */}
      {!isMobile && (
        <>
          <button
            onClick={handlePrevSlide}
            className='absolute left-4 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center text-white transition-all z-20'
            aria-label='Previous slide'
          >
            <ChevronLeft className='h-5 w-5' />
          </button>
          <button
            onClick={handleNextSlide}
            className='absolute right-4 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center text-white transition-all z-20'
            aria-label='Next slide'
          >
            <ChevronRight className='h-5 w-5' />
          </button>
        </>
      )}

      {/* Slide indicators (dots) */}
      <div className='absolute top-4 right-4 flex flex-col space-y-2 z-20'>
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goToSlide(i)}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
              i === currentSlide ? "bg-white" : "bg-white/50"
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
